import { useCallback, useEffect, useMemo, useState } from 'react';
import { errorMessage } from '../api/client';
import {
  PAYMENT_METHODS,
  amount,
  billNumberOf,
  isCancelRequested,
  isEdited,
  isVoided,
  paymentSummary,
  posApi,
  saleTotalOf,
  type BillStatusFilter,
  type PaymentMethod,
  type PosSale,
  type SalesHistory,
} from '../api/pos';
import { billDateTime, money } from '../format';
import { useAuth } from '../auth/AuthContext';
import InvoiceView from '../components/InvoiceView';
import BillTitles from '../components/BillTitles';
import SalesReport from '../components/SalesReport';
import WholeShopToggle, { BillBy, useCoversWholeShop } from '../components/WholeShopToggle';

const PAGE_SIZE = 25;

/** `YYYY-MM-DD` for a date N days ago, in the browser's own timezone (IST at the till). */
function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

const RANGE_PRESETS: { label: string; from: () => string; to: () => string }[] = [
  { label: 'Today', from: () => daysAgo(0), to: () => daysAgo(0) },
  { label: 'Last 7 days', from: () => daysAgo(6), to: () => daysAgo(0) },
  { label: 'Last 30 days', from: () => daysAgo(29), to: () => daysAgo(0) },
];

const STATUS_OPTIONS: { key: BillStatusFilter; label: string }[] = [
  { key: 'all', label: 'All bills' },
  { key: 'live', label: 'Paid only' },
  { key: 'voided', label: 'Cancelled only' },
];

/**
 * Past bills, for looking one up rather than counting the drawer.
 *
 * Deliberately separate from Today's bills: that screen is a cash-up and shows
 * the whole day at once, while this one filters and pages. Voided bills are
 * included by default — somebody hunting for a bill does not yet know whether
 * it was reversed.
 */
export default function HistoryScreen() {
  const { can } = useAuth();
  const [from, setFrom] = useState(() => daysAgo(6));
  const [to, setTo] = useState(() => daysAgo(0));
  const [method, setMethod] = useState<PaymentMethod | ''>('');
  const [status, setStatus] = useState<BillStatusFilter>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [allStaff, setAllStaff] = useState(false);
  const wholeShop = useCoversWholeShop(allStaff);

  const [data, setData] = useState<SalesHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<PosSale | null>(null);
  const [report, setReport] = useState(false);

  // A bill number is long; waiting for a pause beats a request per character.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => window.clearTimeout(t);
  }, [search]);

  // Any filter change puts us back on page 1 — staying on page 4 of a result
  // set that now has two pages just shows an empty list.
  useEffect(() => {
    setPage(1);
  }, [from, to, method, status, allStaff]);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      posApi
        .salesHistory(
          {
            from: from || undefined,
            to: to || undefined,
            paymentMethod: method || undefined,
            status,
            search: debouncedSearch,
            page,
            limit: PAGE_SIZE,
            allStaff,
          },
          signal,
        )
        .then((res) => {
          setData(res);
          setError(null);
        })
        .catch((err: unknown) => {
          if (signal?.aborted) return;
          setError(errorMessage(err, 'Could not load the bill history.'));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [from, to, method, status, debouncedSearch, page, allStaff],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  async function openBill(sale: PosSale) {
    try {
      const full = await posApi.saleById(sale.id);
      setOpen(full ?? sale);
    } catch {
      setOpen(sale);
    }
  }

  const applyPreset = (preset: (typeof RANGE_PRESETS)[number]) => {
    setFrom(preset.from());
    setTo(preset.to());
  };

  const activePreset = useMemo(
    () => RANGE_PRESETS.find((p) => p.from() === from && p.to() === to)?.label ?? null,
    [from, to],
  );

  const bills = data?.bills ?? [];
  const summary = data?.summary;
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="history">
      <div className="filters">
        <div className="filter-top">
          <div className="filter-presets">
            {RANGE_PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                className={activePreset === p.label ? 'chip chip-active' : 'chip'}
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="filter-actions">
            <WholeShopToggle checked={allStaff} onChange={setAllStaff} />
            {can('pos.reports') && (
              <button type="button" className="btn" onClick={() => setReport(true)}>
                Take report
              </button>
            )}
          </div>
        </div>

        <div className="filter-row">
          <label className="field">
            <span>From</span>
            <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="field">
            <span>To</span>
            <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
          </label>
          <label className="field">
            <span>Payment</span>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value as PaymentMethod | '')}
            >
              <option value="">Any</option>
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as BillStatusFilter)}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field field-grow">
            <span>Bill number</span>
            <input
              type="search"
              value={search}
              placeholder="e.g. BLS-1234"
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
        </div>
      </div>

      {summary && (
        <div className="cashup">
          <div>
            <span className="cashup-label">Bills</span>
            <strong>{summary.count}</strong>
          </div>
          <div>
            <span className="cashup-label">Cancelled</span>
            <strong>{summary.voided}</strong>
          </div>
          <div>
            {/* Copies across the whole filtered range, not this page — the
                same rule the money beside it follows. */}
            <span className="cashup-label">Books sold</span>
            <strong>{summary.quantity}</strong>
          </div>
          <div className="cashup-gross">
            <span className="cashup-label">Total taken</span>
            <strong>{money(amount(summary.gross))}</strong>
          </div>
        </div>
      )}

      {error && (
        <div>
          <p className="error">{error}</p>
          <button type="button" className="btn" onClick={() => load()}>
            Try again
          </button>
        </div>
      )}

      {loading ? (
        <p className="muted">Loading bills…</p>
      ) : bills.length === 0 ? (
        <p className="muted empty">
          {debouncedSearch
            ? `No bill matches “${debouncedSearch}”.`
            : 'No bills in this range.'}
        </p>
      ) : (
        <>
          <div className="bill-head" aria-hidden="true">
            <span>Bill</span>
            <span>Date &amp; time</span>
            <span>Payment</span>
            <span className="bill-head-amount">Amount</span>
          </div>
          <ul className="bill-list">
            {bills.map((sale) => {
              const voided = isVoided(sale);
              return (
                <li key={sale.id}>
                  <button
                    type="button"
                    className={voided ? 'bill-row bill-void' : 'bill-row'}
                    onClick={() => openBill(sale)}
                  >
                    <span className="bill-no">
                      {billNumberOf(sale)}
                      {voided && <span className="void-tag">CANCELLED</span>}
                      {!voided && isCancelRequested(sale) && (
                        <span className="cancel-req-tag">CANCEL REQUESTED</span>
                      )}
                      {!voided && isEdited(sale) && (
                        <span className="edit-tag">EDITED</span>
                      )}
                    </span>
                    <span className="bill-time">
                      {billDateTime(sale.createdAt)}
                      {wholeShop && <BillBy name={sale.counterStaff?.fullName} />}
                    </span>
                    <span className="bill-method">{paymentSummary(sale) ?? ''}</span>
                    <span className="bill-total">{money(saleTotalOf(sale))}</span>
                    <BillTitles sale={sale} />
                  </button>
                </li>
              );
            })}
          </ul>

          {totalPages > 1 && (
            <div className="pager">
              <button
                type="button"
                className="btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className="muted">
                Page {page} of {totalPages} · {data?.total ?? 0} bills
              </span>
              <button
                type="button"
                className="btn"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {open && (
        <InvoiceView
          sale={open}
          onClose={() => setOpen(null)}
          onEdited={(updated) => {
            // The edited bill stays on screen to print from; the list
            // behind is reloaded, since its totals for the range have moved.
            setOpen(updated);
            load();
          }}
        />
      )}
      {/* Opens on the period already being looked at, and carries its own date
          pair so any other period can be reported without disturbing the
          filters behind it. */}
      {report && (
        <SalesReport from={from} to={to} rangePicker onClose={() => setReport(false)} />
      )}
    </div>
  );
}
